// Clickable map of everything in the admin dashboard, grouped by category
// -- see src/lib/adminSiteMap.ts for the data and src/lib/adminNav.ts for
// how a click deep-links into a nested tab.
//
// Layout matches Fleet Defaults: one sidebar (categories) + one content
// pane. Items and their sub-items stack as vertical sections inside that
// single pane -- no cascading side-by-side sidebars.
import React, { useState } from "react";
import { Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import SettingsSidebarLayout from "@/components/ui/settings-sidebar-layout";
import { navigateAdmin } from "@/lib/adminNav";
import { ADMIN_SITE_MAP, type SiteMapNode } from "@/lib/adminSiteMap";

const SiteMapSection: React.FC<{ node: SiteMapNode; depth: number }> = ({ node, depth }) => (
  <div className={depth > 0 ? "mt-3 pl-4 border-l border-border/60" : ""}>
    <div className="rounded-lg border border-border bg-zinc-900/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className={depth === 0 ? "text-base font-semibold text-foreground" : "text-sm font-semibold text-foreground"}>
          {node.label}
        </h4>
        {node.navigate ? (
          <Button
            size="sm"
            className="golden-button"
            onClick={() => navigateAdmin(node.navigate!.tab, undefined, node.navigate!.path)}
          >
            Go to {node.label}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground italic">Opens from a specific record</span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-1">{node.description}</p>
    </div>
    {!!node.children?.length && (
      <div className="space-y-3 mt-3">
        {node.children.map((child) => (
          <SiteMapSection key={child.label} node={child} depth={depth + 1} />
        ))}
      </div>
    )}
  </div>
);

const AdminSiteMap: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState(ADMIN_SITE_MAP[0]?.label ?? "");
  const category = ADMIN_SITE_MAP.find((c) => c.label === activeCategory) ?? ADMIN_SITE_MAP[0];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <MapIcon className="h-5 w-5 text-yellow-500" />
          Site Map
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Everything in the admin dashboard, grouped and described. Pick a section, then click "Go to" wherever it shows up.
        </p>
      </div>

      <SettingsSidebarLayout
        tabs={ADMIN_SITE_MAP.map((c) => ({ value: c.label, label: c.label, description: c.description, icon: c.icon }))}
        value={activeCategory}
        onValueChange={setActiveCategory}
        minHeight="min-h-[520px]"
      >
        {category && (
          <div className="space-y-3">
            {category.items.map((item) => (
              <SiteMapSection key={item.label} node={item} depth={0} />
            ))}
          </div>
        )}
      </SettingsSidebarLayout>
    </div>
  );
};

export default AdminSiteMap;
